import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { ArrowRight, Play, Ruler, ListTodo, BadgeCheck, FileText, Check, CheckCheck } from "lucide-react";
import { scrollToId } from "../../lib/scroll";
import { StoreBadges } from "./StoreBadges";

const SCRIPT = [
  { from: "james", text: "Morning! The opening is 4200mm — measured twice.", chip: { icon: Ruler, label: "Saved to Measurements · 4200mm" } },
  { from: "me", text: "Perfect. Can you get the motors ordered by Friday?", chip: { icon: ListTodo, label: "Task created · Due Friday" } },
  { from: "james", text: "Yep, happy with that 👍", chip: { icon: BadgeCheck, label: "Approved · James · 09:41" } },
  { from: "me", text: "Brilliant — sending the drawings over now.", chip: { icon: FileText, label: "gate-drawings.pdf · Saved to Documents" } },
];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Bubble = ({ msg }) => {
  const mine = msg.from === "me";
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 18, scale: 0.94 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      className={`flex flex-col ${mine ? "items-end" : "items-start"}`}
    >
      <div
        className={`max-w-[82%] rounded-2xl px-3.5 py-2.5 text-[13px] leading-snug shadow-sm ${
          mine
            ? "bg-gradient-to-r from-[#1E3A8A] to-[#38BDF8] text-white rounded-br-md"
            : "bg-diem-mist text-diem-ink rounded-bl-md"
        }`}
      >
        {msg.text}
        {mine && (
          <span className="inline-flex ml-1.5 align-middle opacity-80">
            <CheckCheck className="w-3.5 h-3.5" />
          </span>
        )}
      </div>
      {msg.chip && (
        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: 6 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ delay: 0.55, type: "spring", stiffness: 320, damping: 20 }}
          className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-white border border-diem-blue/25 px-2.5 py-1 shadow-[0_4px_14px_rgba(0,85,255,0.12)]"
        >
          <msg.chip.icon className="w-3 h-3 text-diem-blue" />
          <span className="text-[10px] font-bold tracking-wide text-diem-blue">{msg.chip.label}</span>
        </motion.div>
      )}
    </motion.div>
  );
};

const Phone = () => {
  const [step, setStep] = useState(0);

  useEffect(() => {
    const delay = step >= SCRIPT.length ? 4200 : 1600;
    const t = setTimeout(() => setStep((s) => (s >= SCRIPT.length ? 0 : s + 1)), delay);
    return () => clearTimeout(t);
  }, [step]);

  return (
    <div className="relative w-[300px] sm:w-[330px] rounded-[3rem] border border-diem-line bg-white shadow-[0_40px_90px_-20px_rgba(15,23,42,0.3)] p-2.5" data-testid="hero-phone-mockup">
      <div className="rounded-[2.4rem] bg-white overflow-hidden border border-diem-line/60">
        <div className="relative flex items-center gap-2.5 px-4 pt-4 pb-3 border-b border-diem-line/70 bg-white/90">
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-20 h-5 bg-diem-ink rounded-full" />
          <img
            src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBwb3J0cmFpdCUyMGhlYWRzaG90fGVufDB8fHx8MTc4Nzc2OTIzOXww&ixlib=rb-4.1.0&q=85&w=96"
            alt="James, contractor"
            className="w-9 h-9 rounded-full object-cover mt-3"
            loading="lazy"
          />
          <div className="mt-3">
            <p className="text-[13px] font-bold text-diem-ink leading-tight">James · Oakfield Drive</p>
            <p className="text-[11px] text-diem-ok font-semibold flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-diem-ok inline-block" /> Online
            </p>
          </div>
        </div>

        <div className="h-[380px] px-3.5 py-4 flex flex-col justify-end gap-3 bg-[linear-gradient(180deg,#F8FAFC_0%,#FFFFFF_30%)]">
          <AnimatePresence mode="popLayout">
            {SCRIPT.slice(0, step).map((m) => (
              <Bubble key={m.text} msg={m} />
            ))}
          </AnimatePresence>
          {step === 0 && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center text-[11px] text-diem-muted font-medium">
              A normal conversation. Nothing lost.
            </motion.p>
          )}
        </div>

        <div className="px-3.5 py-3 border-t border-diem-line/70 flex items-center gap-2">
          <div className="flex-1 rounded-full bg-diem-mist px-4 py-2.5 text-[12px] text-diem-muted font-medium">
            Message James…
          </div>
          <div className="w-9 h-9 rounded-full bg-diem-blue flex items-center justify-center shadow-[0_6px_16px_rgba(0,85,255,0.4)]">
            <ArrowRight className="w-4 h-4 text-white" />
          </div>
        </div>
      </div>

      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-10 top-24 hidden sm:flex items-center gap-2 rounded-2xl bg-white border border-diem-line px-3.5 py-2.5 shadow-[0_16px_40px_rgba(15,23,42,0.14)]"
      >
        <span className="w-7 h-7 rounded-full bg-diem-ok/10 flex items-center justify-center">
          <Check className="w-4 h-4 text-diem-ok" />
        </span>
        <div>
          <p className="text-[11px] font-bold text-diem-ink">Nothing to file</p>
          <p className="text-[10px] text-diem-muted">Deyem did it for you</p>
        </div>
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative hero-glow overflow-hidden pt-[72px]" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-28 pb-20 lg:pb-28 grid lg:grid-cols-12 gap-14 lg:gap-8 items-center">
        <motion.div style={{ opacity: fade }} className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mb-8"
          >
            <StoreBadges />
          </motion.div>

          <h1 className="font-display font-medium tracking-tighter leading-[0.95] text-[17vw] sm:text-8xl lg:text-[7.5rem] text-diem-ink" data-testid="hero-headline">
            <MaskedLine delay={0.2}>Send me</MaskedLine>
            <MaskedLine delay={0.34}>
              a <span className="text-diem-blue">Deyem.</span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, filter: "blur(10px)", y: 14 }}
            animate={{ opacity: 1, filter: "blur(0px)", y: 0 }}
            transition={{ duration: 0.9, delay: 0.75 }}
            className="mt-7 font-display text-2xl sm:text-3xl font-medium tracking-tight text-diem-ink"
            data-testid="hero-tagline"
          >
            Messaging that remembers.
          </motion.p>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9 }}
            className="mt-5 max-w-xl text-base sm:text-lg leading-relaxed text-diem-slate"
          >
            Message like you always have. Deyem keeps the important stuff organised — tasks, deadlines,
            approvals, photos, documents and more — without turning your conversation into complicated
            project software.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-waitlist-cta"
              onClick={() => scrollToId("#waitlist")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-diem-blue text-white font-semibold px-8 py-4 text-base shadow-[0_12px_36px_rgba(0,85,255,0.4)] hover:shadow-[0_18px_48px_rgba(0,85,255,0.5)] hover:-translate-y-0.5 active:scale-[0.98] transition-[box-shadow,transform] duration-300"
            >
              Join the waitlist
              <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <button
              data-testid="hero-how-it-works-cta"
              onClick={() => scrollToId("#how-it-works")}
              className="group inline-flex items-center gap-2.5 rounded-full bg-white border border-diem-line text-diem-ink font-semibold px-8 py-4 text-base hover:bg-diem-paper hover:border-diem-blue/30 active:scale-[0.98] transition-[background-color,border-color,transform] duration-300"
            >
              <Play className="w-4 h-4 text-diem-blue fill-diem-blue" />
              See how it works
            </button>
          </motion.div>
        </motion.div>

        <motion.div
          style={{ y: phoneY, rotate: phoneRotate }}
          className="lg:col-span-5 flex justify-center lg:justify-end"
        >
          <motion.div
            initial={{ opacity: 0, y: 80, rotate: 3 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            transition={{ duration: 1.2, delay: 0.55, ease: [0.16, 1, 0.3, 1] }}
          >
            <Phone />
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
